<template>
  <div class="form-container">
    <div class="editor-row">
      <label class="label-editor" for="delivery-date-picker">{{ t('Nova data') }}:</label>
      <InputDate
        id="delivery-date-picker"
        v-model="newDate"
        date-format="dd/mm/yy"
        data-testid="delivery-date-picker"
      />
    </div>

    <div class="reason-field">
      <label class="label-reason" for="delivery-date-reason">{{ t('Motivo') }}:</label>
      <input-text-area
        elem-id="delivery-date-reason"
        v-model="reason"
        :placeholder="t('Justificativa obrigatória para alteração')"
        :rows="2"
        :class="{ 'p-invalid': showReasonError && !reason }"
        data-testid="delivery-date-reason"
      />
      <small v-if="showReasonError && !reason" class="error-message">
        {{ t('Motivo é obrigatório') }}
      </small>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSODeliveryDate } from '../../composables/useSODeliveryDate';
import { useDateFormatter } from '../../composables/useDateFormatter';
import type { Orders_UpdateItemDeliveryDateResponseDados } from '../../contracts/Orders_UpdateItemDeliveryDateResponse';

const { t } = useI18n();
// InputDate emits a YYYY-MM-DD string (not a Date). Parse it to a local-midnight
// Date before calling toApiDateTimeFormat — otherwise `new Date("YYYY-MM-DD")`
// parses as UTC midnight and the GMT-3 conversion shifts the date -1 day.
const { parseBackendDate, toApiDateTimeFormat } = useDateFormatter();
const { updateDeliveryDate } = useSODeliveryDate();

const props = defineProps<{
  orderItemId: number;
}>();

const emit = defineEmits<{
  'submit-success': [result: Orders_UpdateItemDeliveryDateResponseDados];
  'submit-error': [err: unknown];
}>();

const newDate = ref<string>('');
const reason = ref('');
const showReasonError = ref(false);

const canSubmit = computed(() => !!newDate.value && reason.value.trim().length > 0);

function validateForm(): boolean {
  showReasonError.value = !reason.value.trim();
  return canSubmit.value;
}

function resetForm(): void {
  newDate.value = '';
  reason.value = '';
  showReasonError.value = false;
}

async function handleSubmit(): Promise<void> {
  if (!validateForm()) return;
  const localDate = parseBackendDate(newDate.value);
  if (!localDate) {
    emit('submit-error', new Error('Invalid date'));
    return;
  }
  try {
    const result = await updateDeliveryDate(props.orderItemId, {
      expectedDeliveryAt: toApiDateTimeFormat(localDate),
      reason: reason.value.trim(),
    });
    if (result) {
      emit('submit-success', result);
      resetForm();
    }
  } catch (err) {
    emit('submit-error', err);
  }
}

defineExpose({ handleSubmit, resetForm, validateForm, canSubmit });
</script>

<style scoped>
.form-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.editor-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.label-editor,
.label-reason {
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--p-text-color);
}

.reason-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.error-message {
  color: var(--p-red-500);
  font-size: 0.75rem;
}
</style>
